import { useRouter } from '@tanstack/react-router';
import { PageLoader } from '@customerio/pluma-components/react';
import Journeys from '@/pages/journeys';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { MigrationRouteView } from '@/services/router/migration-route-view';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import { useCurrentWorkspace } from '@/context/current-workspace';
import PeopleListPage from './index.page';

const LOADING_LABEL = 'Loading…';

/**
 * The old `/journeys/people` and `/journeys/profiles` URLs (the Ember router
 * flag-swaps which segment is canonical per account). Flag off → the embedded
 * Ember app still owns the page and renders here. Flag on → Hydra claims the
 * URL purely to forward it to the migrated route, so Ember-side links keep
 * working without being re-pointed.
 */
export function JourneysPeopleHandoff() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const router = useRouter();
  const to = useMigrationHandoffHref(
    (workspaceId) => router.buildLocation({ to: '/workspaces/$workspaceId/profiles', params: { workspaceId } }).href,
  );

  if (!isMigrated) {
    return <Journeys />;
  }

  if (to == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}

/**
 * The migrated `/profiles` URL. Flag on → Hydra owns the page. Flag off → this
 * URL has no Ember counterpart (Ember only knows the `journeys` forms), so
 * hand back to the `journeys` twin, which Ember serves (canonicalizing the
 * segment itself) — the exact inverse of `JourneysPeopleHandoff`. The two
 * can't loop: each fires in the opposite flag state.
 */
export function PeopleListRouteView() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const router = useRouter();
  const legacyHref = useMigrationHandoffHref(
    (workspaceId) =>
      router.buildLocation({ to: '/workspaces/$workspaceId/journeys/people', params: { workspaceId } }).href,
  );
  // Readiness gates on the resolved workspace, not the URL segment: the
  // page's reads go through `requireWorkspaceId`, which throws while the
  // workspaces list is still loading.
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      <PeopleListPage />
    </MigrationRouteView>
  );
}
